import { BaseResponse } from '@kts-front/types';

import { ENDPOINTS } from 'configs/endpoints';
import { clientSchema } from 'entities/Client';
import { ClientEntity } from 'entities/Client';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { ClientUniquenessStore } from 'stores/global/ClientUniquenessStore';
import { notificationsStore, rootStore } from 'stores/global/RootStore';
import { ClientEditFieldsKey } from 'stores/global/RootStore/AppConfigStore';
import { EditFormStore, EditFormStoreOptions } from 'stores/local/shared/EntityEditPopupStore/EditFormStore';
import { FieldPhoneStore } from 'stores/local/shared/form/FieldPhoneStore';
import { FieldTextStore } from 'stores/local/shared/form/FieldTextStore';
import { CommonApiError } from 'types/ApiError';
import { validateEmail, validatePhoneNumber, validateIf } from 'utils/validators';

import { DefaultValues } from './types';

type FieldsMap = {
  [ClientEditFieldsKey.phone]: FieldPhoneStore;
  [ClientEditFieldsKey.firstName]: FieldTextStore;
  [ClientEditFieldsKey.lastName]: FieldTextStore;
  [ClientEditFieldsKey.patronymic]: FieldTextStore;
  [ClientEditFieldsKey.email]: FieldTextStore;
};

export type ClientEditFormStoreOptions = EditFormStoreOptions & {
  checkUniqueClientStore?: ClientUniquenessStore;
};

export class ClientEditFormStore extends EditFormStore<DefaultValues, FieldsMap> {
  readonly fieldsMap: FieldsMap;

  private readonly _createRequest = apiStore.createRequest({
    url: ENDPOINTS.client.create,
    method: 'POST',
  });

  private readonly _editRequest = apiStore.createRequest({
    url: ENDPOINTS.client.edit,
    method: 'POST',
  });

  private readonly _checkUniqueClientStore: ClientUniquenessStore;

  constructor({ onSuccess, checkUniqueClientStore }: ClientEditFormStoreOptions) {
    super({ onSuccess });

    this._checkUniqueClientStore =
      checkUniqueClientStore ??
      new ClientUniquenessStore({
        checkUniquePhone: rootStore.appConfigStore.logic.checkUniquePhone,
      });

    this.fieldsMap = {
      [ClientEditFieldsKey.phone]: new FieldPhoneStore({
        required: !rootStore.appConfigStore.logic.clientCreation.optionalFields.has(ClientEditFieldsKey.phone),
        validator: validatePhoneNumber(),
        checkUniqueClientStore: this._checkUniqueClientStore,
      }),
      [ClientEditFieldsKey.firstName]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.clientCreation.optionalFields.has(ClientEditFieldsKey.firstName),
      }),
      [ClientEditFieldsKey.lastName]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.clientCreation.optionalFields.has(ClientEditFieldsKey.lastName),
      }),
      [ClientEditFieldsKey.patronymic]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.clientCreation.optionalFields.has(ClientEditFieldsKey.patronymic),
      }),
      [ClientEditFieldsKey.email]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.clientCreation.optionalFields.has(ClientEditFieldsKey.email),
        validator: validateIf(() => Boolean(this.fieldsMap[ClientEditFieldsKey.email].value), validateEmail()),
      }),
    };
  }

  reset(): void {
    super.reset();
    this.fieldsMap[ClientEditFieldsKey.phone].setUniqueResult(null);
  }

  init(defaultValues: DefaultValues): void {
    super.init(defaultValues);

    if (defaultValues.phone) {
      this.fieldsMap[ClientEditFieldsKey.phone].setValue(defaultValues.phone, { noTouch: true });
      this.fieldsMap[ClientEditFieldsKey.phone].setDisabled(true);
    }

    if (defaultValues.firstName) {
      this.fieldsMap[ClientEditFieldsKey.firstName].setValue(defaultValues.firstName, { noTouch: true });
    }

    if (defaultValues.lastName) {
      this.fieldsMap[ClientEditFieldsKey.lastName].setValue(defaultValues.lastName, { noTouch: true });
    }

    if (defaultValues.patronymic) {
      this.fieldsMap[ClientEditFieldsKey.patronymic].setValue(defaultValues.patronymic, { noTouch: true });
    }

    if (defaultValues.email) {
      this.fieldsMap[ClientEditFieldsKey.email].setValue(defaultValues.email, { noTouch: true });
    }
  }

  async submit(): Promise<void> {
    if (!this.validateFields()) {
      return;
    }

    if (this.id === null) {
      const result = await this._createClient();

      if (!result.isError) {
        this._options.onSuccess({ isCreated: true, createdClient: result.data });
      }

      return;
    }

    const isSuccess = await this._editClient();

    if (isSuccess) {
      this._options.onSuccess({ isCreated: false });
    }
  }

  private async _createClient(): Promise<BaseResponse<ClientEntity>> {
    this.loadingStage.loading();

    const requestPayload = this.fieldsKeys.reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = this.fieldsMap[key].serverFormat;

      return acc;
    }, {});

    const response = await this._createRequest.call({
      data: requestPayload,
    });

    if (response.isError) {
      logApiErrWithSentry({
        endpoint: ENDPOINTS.client.create,
        method: 'POST',
        requestPayload,
        response,
      });
      const fields = response.data?.data?.fields ?? [];

      if (fields.length > 0) {
        fields.forEach((f: { field: string; message: string }) => {
          this.setFieldSyntheticError(f.field, f.message);
        });
        notificationsStore.showError({ message: response.data?.data?.message, autoClose: 6000 });
      } else {
        notificationsStore.showErrorByResponse(response);
      }

      this.loadingStage.error();

      return { isError: true };
    }

    const parsed = clientSchema.safeParse(response.data);

    if (parsed.error) {
      this.loadingStage.error();
      notificationsStore.showError({ message: parsed.error.message });

      return { isError: true };
    }

    notificationsStore.showSuccess('Клиент успешно создан');

    this.loadingStage.success();

    return { isError: false, data: parsed.data };
  }

  private async _editClient(): Promise<boolean> {
    this.loadingStage.loading();

    if (!this.id) {
      return false;
    }

    const requestPayload = this.fieldsKeys.reduce<Record<string, unknown>>(
      (acc, key) => {
        acc[key] = this.fieldsMap[key].serverFormat;

        return acc;
      },
      {
        id: this.id,
      },
    );

    const response = await this._editRequest.call<any, CommonApiError>({ data: requestPayload });

    if (response.isError) {
      logApiErrWithSentry({
        endpoint: ENDPOINTS.client.edit,
        method: 'POST',
        requestPayload,
        response,
      });
      const fields = response.data?.data?.fields ?? [];

      if (fields.length > 0) {
        fields.forEach((f) => {
          this.setFieldSyntheticError(f.field, f.message);
        });
      } else {
        notificationsStore.showErrorByResponse(response);
      }

      this.loadingStage.error();

      return false;
    }

    notificationsStore.showSuccess('Клиент успешно отредактирован');

    this.loadingStage.success();

    return true;
  }
}
